import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./writeReview.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import * as kagekiri from 'kagekiri';
import equalityFn from 'zustand/shallow';
import { DeleteReviewStatus, WriteReviewStatus } from '~/modules/services/reviewService';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { reviewStore } from '~/modules/stores/reviewStore';
import { confirmPrompt } from '~/modules/utilities/browser';
import { handleException } from '~/modules/utilities/error_utilities';
import { sendNotification } from '~/modules/utilities/notificationUtilities';
class WriteReview extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listingId = void 0;
    this.closedFocusTarget = void 0;
    this.autoFocusOff = void 0;
    this.allowRatingText = true;
    this.formData = {
      rating: 0,
      title: '',
      description: ''
    };
    this.starTextRating = {
      rating_1: i18nStore.getState().i18n.starRatingText.oneStarRating(),
      rating_2: i18nStore.getState().i18n.starRatingText.twoStarRating(),
      rating_3: i18nStore.getState().i18n.starRatingText.threeStarRating(),
      rating_4: i18nStore.getState().i18n.starRatingText.fourStarRating(),
      rating_5: i18nStore.getState().i18n.starRatingText.fiveStarRating()
    };
    this.writeReviewStatus = WriteReviewStatus.AVAILABLE;
    this.deleteReviewStatus = DeleteReviewStatus.AVAILABLE;
    this.errorMsg = '';
    this.forceRatingError = false;
    this.forceTitleError = false;
    this.forceDescriptionError = false;
    this.inputsChanged = false;
  }
  i18nCallback(i18n) {
    this.sharedLabels = i18n.shared;
    this.writeReviewModalLabels = i18n.modals.writeReview;
    const {
      rating,
      title,
      description
    } = this.writeReviewModalLabels.formInputs;
    this.formFields = {
      rating,
      title,
      description
    };
  }
  setCurrentReviews(reviewState) {
    this.writeReviewStatus = reviewState.writeReviewStatus;
    this.deleteReviewStatus = reviewState.deleteReviewStatus;
    this.errorMsg = reviewState.errorMsg;
    if (reviewState.userReview?.questionnaireResponseId) {
      const reviewValues = reviewStore.getReviewValues();
      this.userReview = reviewState.userReview;
      if (!this.inputsChanged) {
        const {
          rating,
          title,
          description
        } = reviewValues;
        this.formData = {
          rating: Number(rating),
          title,
          description
        };
      }
    }
  }
  get isWritingOrDeleting() {
    return this.writeReviewStatus === WriteReviewStatus.IN_PROGRESS || this.deleteReviewStatus === DeleteReviewStatus.IN_PROGRESS;
  }
  get isEditMode() {
    return Boolean(this.userReview?.questionnaireResponseId);
  }
  get submitReviewLabel() {
    if (this.isEditMode) {
      return this.sharedLabels.update;
    }
    return this.sharedLabels.share;
  }
  get shouldSubmitBeDisabled() {
    const requiredFields = ['title', 'rating', 'description'];
    // @ts-expect-error * Automated Type Error Fix
    return requiredFields.every(field => Boolean(this.formData[field]));
  }
  get deleteReviewButton() {
    return kagekiri.querySelector('.delete-review-button');
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTitleChange(event) {
    this.formData.title = event.target.value;
    this.inputsChanged = true;
  }

  // @ts-expect-error * Automated Type Error Fix
  handleDescriptionChange(event) {
    this.formData.description = event.target.value;
    this.inputsChanged = true;
  }
  handleRating(event) {
    this.formData.rating = Number(event.detail.rating);
    this.inputsChanged = true;
  }
  get ratingText() {
    return this.starTextRating[`rating_${this.formData.rating}`] || '';
  }
  triggerInputErrors() {
    const {
      title,
      rating,
      description
    } = this.formData;
    if (!rating) {
      this.forceRatingError = true;
    }
    if (!title) {
      this.forceTitleError = true;
    }
    if (!description) {
      this.forceDescriptionError = true;
    }
  }
  async handleSubmitReview() {
    if (this.shouldSubmitBeDisabled) {
      const action = this.isEditMode ? reviewStore.editReview : reviewStore.createReview;
      const review = this.formData;
      const response = await action(this.listingId, {
        ...review,
        rating: review.rating.toString()
      });
      if (response.isSuccess) {
        const {
          successMessage
        } = response;
        // @ts-expect-error * Automated Type Error Fix
        sendNotification(successMessage, {
          previousFocusedElement: this.closedFocusTarget
        });
        this.closeModal();
      } else {
        const {
          // @ts-expect-error * Automated Type Error Fix
          exceptionInfo: {
            error,
            context
          }
        } = response;
        handleException(error, context);
      }
    } else {
      this.triggerInputErrors();
    }
  }
  async deleteReview() {
    const confirmTitle = this.writeReviewModalLabels.deleteReviewConfirmTitle;
    const confirmMessage = this.writeReviewModalLabels.deleteReviewConfirmText;
    const confirmOptions = {
      confirmButtonLabel: this.sharedLabels.delete,
      previousFocusedElement: this.deleteReviewButton
    };
    if (await confirmPrompt(confirmTitle, confirmMessage, confirmOptions)) {
      const response = await reviewStore.deleteReview(this.listingId);
      const previousFocusedElementNotification = this.closedFocusTarget;
      if (response.isSuccess) {
        const {
          successMessage
        } = response;
        // @ts-expect-error * Automated Type Error Fix
        sendNotification(successMessage, {
          previousFocusedElement: previousFocusedElementNotification
        });
        this.closeModal();
      } else {
        const {
          // @ts-expect-error * Automated Type Error Fix
          exceptionInfo: {
            error: err,
            context
          }
        } = response;
        handleException(err, context);
      }
    }
  }
  closeModal() {
    reviewStore.resetErrorMsg();
    this.dispatchEvent(new CustomEvent('close'));
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(WriteReview, {
  publicProps: {
    listingId: {
      config: 0
    },
    closedFocusTarget: {
      config: 0
    },
    autoFocusOff: {
      config: 0
    }
  },
  track: {
    formData: 1
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    },
    setCurrentReviews: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: reviewStore,
          // @ts-expect-error * Automated Type Error Fix
          selector: ({
            writeReviewStatus,
            deleteReviewStatus,
            userReview,
            errorMsg
          }) => ({
            writeReviewStatus,
            deleteReviewStatus,
            userReview,
            errorMsg
          }),
          equalityFn
        };
      }
    }
  },
  fields: ["allowRatingText", "starTextRating", "writeReviewStatus", "deleteReviewStatus", "errorMsg", "forceRatingError", "forceTitleError", "forceDescriptionError", "inputsChanged"]
});
const __lwc_component_class_internal = _registerComponent(WriteReview, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;